/**
 * Home's illustrations, each composed from a few Fluent 3D emoji on a square
 * of the size it is given, so one drawing serves a 320pt phone and a desktop
 * browser alike.
 *
 * Every piece is decoration: the card around it carries the words and the
 * screen-reader label, so the drawings are hidden from assistive technology.
 */

import { memo, type ReactNode } from 'react';
import { Image, StyleSheet, View, type ImageStyle, type StyleProp, type ViewStyle } from 'react-native';

import { ART, type ArtName } from '../../assets';
import { colors, radius } from '../../theme';
import { platformSwapsSides } from '../../core/i18n/nativeDirection';

const HIDDEN = {
  accessibilityElementsHidden: true,
  importantForAccessibility: 'no-hide-descendants' as const,
};

/** One image placed on the square, by fractions of its side. */
function Piece({
  name,
  size,
  at,
  style,
}: {
  name: ArtName;
  size: number;
  at: { top?: number; bottom?: number; left?: number; right?: number; side: number };
  style?: StyleProp<ImageStyle>;
}) {
  const side = Math.round(size * at.side);
  const place = (fraction?: number) => (fraction === undefined ? undefined : Math.round(size * fraction));
  const [left, right] = physicalSides(at.left, at.right);
  return (
    <Image
      source={ART[name]}
      resizeMode="contain"
      accessible={false}
      style={[
        styles.piece,
        {
          width: side,
          height: side,
          top: place(at.top),
          bottom: place(at.bottom),
          left: place(left),
          right: place(right),
        },
        style,
      ]}
    />
  );
}

/**
 * A drawing's parts are placed by physical side: a drawing is not text, and
 * does not mirror in Urdu. Android swaps every left and right in a
 * right-to-left app, so there the sides are given swapped, and land back
 * where they were drawn.
 */
function physicalSides<T>(left: T, right: T): [T, T] {
  return platformSwapsSides() ? [right, left] : [left, right];
}

/**
 * The drawing's own square, laid out left to right on iOS, which reads this
 * style (Android does not; see `physicalSides`).
 */
function Square({ size, children, style }: { size: number; children: ReactNode; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={[styles.square, { width: size, height: size }, style]} {...HIDDEN}>
      {children}
    </View>
  );
}

/** A bowl of food held up in two hands, with hearts over it. */
export const HeroArt = memo(function HeroArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="herb" size={size} at={{ top: 0.02, right: -0.06, side: 0.5 }} style={styles.leaf} />
      <Piece
        name="herb"
        size={size}
        at={{ bottom: 0.02, left: -0.1, side: 0.44 }}
        style={[styles.leaf, styles.mirrored]}
      />
      {/* The hands image has a strip of air under the wrists; set below the
          square's foot, the wrists run off the card's edge as in the mockup
          instead of stopping short of it. */}
      <Piece name="palmsUp" size={size} at={{ bottom: -0.08, left: 0.12, side: 0.76 }} />
      <Piece name="curryRice" size={size} at={{ bottom: 0.12, left: 0.15, side: 0.7 }} />
      <Piece name="redHeart" size={size} at={{ top: 0.02, left: 0.5, side: 0.2 }} />
      <Piece name="redHeart" size={size} at={{ top: 0.2, left: 0.16, side: 0.12 }} />
      <Piece name="redHeart" size={size} at={{ top: 0, right: 0.02, side: 0.11 }} />
    </Square>
  );
});

/** A tiffin with a heart on it. */
export const TiffinArt = memo(function TiffinArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="tiffinBox" size={size} at={{ top: 0, left: 0, side: 1 }} />
      <Piece name="redHeart" size={size} at={{ top: 0.5, left: 0.31, side: 0.38 }} />
    </Square>
  );
});

/** A heart offered on an open hand, with a burst of light around it. */
export const SupportArt = memo(function SupportArt({ size }: { size: number }) {
  const ray = (rotate: number, top: number, left: number) => {
    const [fromLeft, fromRight] = physicalSides<number | undefined>(Math.round(size * left), undefined);
    return (
      <View
        style={[
          styles.ray,
          {
            width: Math.max(2, Math.round(size * 0.035)),
            height: Math.round(size * 0.12),
            top: Math.round(size * top),
            left: fromLeft,
            right: fromRight,
            transform: [{ rotate: `${rotate}deg` }],
          },
        ]}
      />
    );
  };
  return (
    <Square size={size}>
      <Piece name="palmUpHand" size={size} at={{ bottom: 0, left: 0, side: 0.9 }} />
      <Piece name="redHeart" size={size} at={{ top: 0.04, left: 0.3, side: 0.46 }} />
      {ray(-40, 0.04, 0.2)}
      {ray(-75, 0.26, 0.12)}
      {ray(40, 0.04, 0.8)}
      {ray(75, 0.26, 0.88)}
    </Square>
  );
});

/** A glowing star. */
export const NewFeaturesArt = memo(function NewFeaturesArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="glowingStar" size={size} at={{ top: 0, left: 0, side: 1 }} />
    </Square>
  );
});

/** A receipt with a clock on it: orders, and where they have got to. */
export const OrdersArt = memo(function OrdersArt({ size }: { size: number }) {
  const badge = Math.round(size * 0.42);
  const border = Math.max(2, Math.round(badge * 0.08));
  // Children are placed inside the border, so the middle is the inner box's.
  const inner = badge - 2 * border;
  const thick = Math.max(2, Math.round(badge * 0.1));
  // A hand turns about its foot at the middle of the face.
  const hand = (length: number, rotate: number) => {
    const long = Math.round(inner * length);
    return (
      <View
        style={[
          styles.hand,
          {
            width: thick,
            height: long,
            bottom: inner / 2,
            left: inner / 2 - thick / 2,
            transform: [
              { translateY: long / 2 },
              { rotate: `${rotate}deg` },
              { translateY: -long / 2 },
            ],
          },
        ]}
      />
    );
  };
  return (
    <Square size={size}>
      <Piece name="receipt" size={size} at={{ top: 0, left: 0, side: 0.9 }} />
      <View
        style={[
          styles.clock,
          { width: badge, height: badge, borderRadius: badge / 2, borderWidth: border },
          // Bottom right of the receipt.
          platformSwapsSides() ? { left: 0 } : { right: 0 },
        ]}
      >
        {hand(0.36, 0)}
        {hand(0.28, 90)}
      </View>
    </Square>
  );
});

/** A soft round wash with a drawing in it: the assurances under the actions. */
export const Blob = memo(function Blob({
  size,
  tint,
  children,
}: {
  size: number;
  tint: string;
  children: (inner: number) => ReactNode;
}) {
  const inner = Math.round(size * 0.68);
  return (
    <View
      style={[styles.blob, { width: size, height: size, borderRadius: size / 2, backgroundColor: tint }]}
      {...HIDDEN}
    >
      {children(inner)}
    </View>
  );
});

export const SaladArt = memo(function SaladArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="greenSalad" size={size} at={{ top: 0, left: 0, side: 1 }} />
    </Square>
  );
});

/** A shield with a tick on it. */
export const SafeArt = memo(function SafeArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="shield" size={size} at={{ top: 0, left: 0.04, side: 0.92 }} />
      <Piece name="checkMark" size={size} at={{ bottom: 0, right: 0, side: 0.46 }} />
    </Square>
  );
});

export const CareArt = memo(function CareArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="redHeart" size={size} at={{ top: 0.04, left: 0.04, side: 0.92 }} />
    </Square>
  );
});

export const BookArt = memo(function BookArt({ size }: { size: number }) {
  return (
    <Square size={size}>
      <Piece name="openBook" size={size} at={{ top: 0, left: 0, side: 1 }} />
    </Square>
  );
});

/** A sprig of herb, faded, for the corners of a card. */
export const LeafArt = memo(function LeafArt({
  size,
  mirrored = false,
  style,
}: {
  size: number;
  mirrored?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <Square size={size} style={style}>
      <Piece
        name="herb"
        size={size}
        at={{ top: 0, left: 0, side: 1 }}
        style={[styles.leaf, mirrored && styles.mirrored]}
      />
    </Square>
  );
});

const styles = StyleSheet.create({
  square: {
    direction: 'ltr',
  },
  piece: {
    position: 'absolute',
  },
  leaf: {
    opacity: 0.35,
  },
  mirrored: {
    transform: [{ scaleX: -1 }],
  },
  ray: {
    position: 'absolute',
    borderRadius: radius.full,
    backgroundColor: colors.text.onBrand,
  },
  clock: {
    position: 'absolute',
    bottom: 0,
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.surface,
  },
  hand: {
    position: 'absolute',
    borderRadius: radius.full,
    backgroundColor: colors.text.onBrand,
  },
  blob: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
